'use client'

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { SendMock } from '../feature-mocks/SendMock'
import { usePrefersReducedMotion } from '../feature-mocks/hooks'
import { ConfettiBurst } from './ConfettiBurst'
import { SendJourneyContext } from './SendJourneyContext'
import { useDesktopViewport } from './useDesktopViewport'
import { DOCK_ARRIVAL_VH, DOCK_OFF_T, DOCK_ON_T, DOCK_PHASE, HERO_PHASE, HERO_TILT_DEG } from './constants'

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t)
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/**
 * Drives the landing-page "send journey": one SendMock starts in the hero
 * collage, pins below the top edge while the page scrolls, scrubs its upload
 * progress with scroll position, and docks into the FeatureZigzag "Sending"
 * slot. Inactive (current static layout) below 901px, under
 * prefers-reduced-motion, and before hydration.
 */
export function SendJourneyProvider({ children }: { children: ReactNode }) {
  const desktop = useDesktopViewport()
  const reduced = usePrefersReducedMotion()
  const enabled = desktop && !reduced

  const heroSlotRef = useRef<HTMLDivElement | null>(null)
  const dockSlotRef = useRef<HTMLDivElement | null>(null)
  const floatRef = useRef<HTMLDivElement | null>(null)
  const [scrubPhase, setScrubPhase] = useState(HERO_PHASE)
  const [docked, setDocked] = useState(false)

  useEffect(() => {
    if (!enabled) return
    const hero = heroSlotRef.current
    const dock = dockSlotRef.current
    const float = floatRef.current
    if (!hero || !dock || !float) return

    let raf = 0
    const update = () => {
      raf = 0
      const scrollY = window.scrollY
      const heroRect = hero.getBoundingClientRect()
      const dockRect = dock.getBoundingClientRect()
      // Journey spans from page top (so the mock detaches on the very first
      // scrolled pixel) to the dock slot reaching mid-viewport — docking that
      // early keeps the confetti burst and link-card reveal fully on screen
      // while the section is still settling toward the top.
      const dockTopDoc = dockRect.top + scrollY
      const journeyEnd = dockTopDoc - window.innerHeight * DOCK_ARRIVAL_VH
      const t = journeyEnd <= 0 ? 1 : clamp01(scrollY / journeyEnd)
      const e = easeOutQuad(t)

      const heroTopDoc = heroRect.top + scrollY
      const top = t >= 1 ? dockRect.top : lerp(heroTopDoc, dockTopDoc - journeyEnd, e)
      const left = lerp(heroRect.left, dockRect.left, e)
      const scale = dockRect.width > 0 ? lerp(heroRect.width / dockRect.width, 1, e) : 1
      const tilt = HERO_TILT_DEG * (1 - e)

      float.style.width = `${dockRect.width}px`
      float.style.transform = `translate3d(${left}px, ${top}px, 0) rotate(${tilt}deg) scale(${scale})`
      float.style.visibility = 'visible'

      setScrubPhase(Math.round(lerp(HERO_PHASE, DOCK_PHASE, t) * 100) / 100)
      setDocked((prev) => (prev ? t > DOCK_OFF_T : t >= DOCK_ON_T))
    }
    const schedule = () => {
      if (raf === 0) raf = requestAnimationFrame(update)
    }

    schedule()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    const ro = new ResizeObserver(schedule)
    ro.observe(hero)
    ro.observe(dock)
    return () => {
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      ro.disconnect()
      if (raf !== 0) cancelAnimationFrame(raf)
    }
  }, [enabled])

  const contextValue = useMemo(() => ({ enabled, docked, heroSlotRef, dockSlotRef }), [enabled, docked])

  return (
    <SendJourneyContext.Provider value={contextValue}>
      {children}
      {enabled && (
        <div
          ref={floatRef}
          aria-hidden
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            transformOrigin: 'top left',
            visibility: 'hidden',
            pointerEvents: 'none',
            zIndex: 30
          }}>
          <SendMock phase={scrubPhase} />
          {docked && <ConfettiBurst />}
        </div>
      )}
    </SendJourneyContext.Provider>
  )
}
